import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  parseUiPreferences,
  persistUiPreferencesLocal,
  saveUiPreferencesToServer,
} from '../config/uiPreferences';

const UiLayoutPreferencesContext = createContext(null);

function layoutFromParsed(parsed) {
  return {
    showSidebarGroups: parsed.showSidebarGroups,
    showSidebarSearch: parsed.showSidebarSearch,
    showSidebarFavorites: parsed.showSidebarFavorites,
  };
}

export function UiLayoutPreferencesProvider({ children }) {
  const [layout, setLayout] = useState(() =>
    layoutFromParsed(parseUiPreferences(null))
  );

  const applyFromStorage = useCallback(() => {
    setLayout(layoutFromParsed(parseUiPreferences(null)));
  }, []);

  useEffect(() => {
    window.addEventListener('ui-preferences-applied', applyFromStorage);
    return () =>
      window.removeEventListener('ui-preferences-applied', applyFromStorage);
  }, [applyFromStorage]);

  const setPreference = useCallback((key, value) => {
    setLayout((prev) => {
      const next = { ...prev, [key]: Boolean(value) };
      persistUiPreferencesLocal({ [key]: next[key] });
      saveUiPreferencesToServer({ [key]: next[key] });
      return next;
    });
  }, []);

  const value = {
    ...layout,
    setShowSidebarGroups: (v) => setPreference('showSidebarGroups', v),
    setShowSidebarSearch: (v) => setPreference('showSidebarSearch', v),
    setShowSidebarFavorites: (v) => setPreference('showSidebarFavorites', v),
  };

  return (
    <UiLayoutPreferencesContext.Provider value={value}>
      {children}
    </UiLayoutPreferencesContext.Provider>
  );
}

export function useUiLayoutPreferences() {
  const ctx = useContext(UiLayoutPreferencesContext);
  if (!ctx) {
    throw new Error(
      'useUiLayoutPreferences must be used within UiLayoutPreferencesProvider'
    );
  }
  return ctx;
}

export default UiLayoutPreferencesContext;
